import type { ErrorReport } from "@mainsail/schema";
import type { ApiService } from "../api/api-service";
import type { AuthService } from "../auth/auth-service";
import type { PromptService } from "../prompt/prompt-service";
import { asError } from "@charterindex/armoury-www";
import { UTCDate } from "@date-fns/utc";
import { observable } from "ecce-react";
import { serializeError } from "serialize-error";
import StackTrace from "stacktrace-js";
import { isGraphqlError, isUnhandledUpstreamError, UnhandledGraphQLError } from "../../util/error";
import { reportErrorMutation } from "./error-service.gql";


export type ErrorSource = "COMPONENT" | "UNCAUGHT_ERROR" | "UNHANDLED_REJECTION" | "REPORT";

export type ErrorDetails = {
	name: string;
	message: string;
	details?: Record<string, unknown>;
	time: string;
	url: string;
	user: string;
	gql?: unknown;
	stack: string[];
};
export class ErrorService {
	readonly #api: ApiService;
	readonly #auth: AuthService;
	readonly #prompt: PromptService;

	#hasError = false;
	get hasError(): boolean { return this.#hasError; }
	@observable() private set hasError(value: boolean) { this.#hasError = value; }

	#error: Error | null = null;
	get error(): Error | null { return this.#error; }
	@observable() private set error(value: Error | null) { this.#error = value; }

	#errorDetails: ErrorDetails | null = null;
	get errorDetails(): ErrorDetails | null { return this.#errorDetails; }
	@observable() private set errorDetails(value: ErrorDetails | null) { this.#errorDetails = value; }

	#errorReported = false;

	constructor(init: { api: ApiService, auth: AuthService, prompt: PromptService; }) {
		this.#api = init.api;
		this.#auth = init.auth;
		this.#prompt = init.prompt;

		if(typeof (window) === "undefined") {
			return;
		}

		window.addEventListener("error", ev => this.handleError("UNCAUGHT_ERROR", ev.error));
		window.addEventListener("unhandledrejection", ev => this.handleError("UNHANDLED_REJECTION", ev.reason));
	}

	#getUrl(): string {
		if(typeof (window) === "undefined") {
			return "";
		}

		return window.location.href;
	}

	#getUser(): string {
		try {
			return this.#auth.activeUser.id;
		} catch(err) {
			return "Unable to find user: " + asError(err).message;
		}
	}

	async #getStack(err: Error): Promise<string[]> {
		if(!err.stack) {
			return [ "(no stacktrace)" ];
		}

		try {
			const stack = (await StackTrace.fromError(err))
				.map(f => `${f.fileName?.replace("/__parcel_source_root/", "") ?? "unknownFile"}:${f.lineNumber ?? "unknownLine"}:${f.columnNumber ?? "unknownColumn"}`);
			return stack;
		} catch(stackErr) {
			return [
				`<<< failed to getStack: ${asError(stackErr).message} >>>`,
				...(err.stack.split("\n") ?? []),
			];
		}
	}

	async handleError(source: ErrorSource, error: unknown): Promise<void> {
		if(isUnhandledUpstreamError(error)) {
			const jsonApiError = error.graphQLError.extensions.jsonApiErrors?.[0];
			if(jsonApiError?.meta?.["endUser"]) {
				await this.#prompt.alert({
					title: jsonApiError.title!,
					message: jsonApiError.detail!,
				});
				return;
			}
		}

		const err = asError(error);

		// Handle navigating to a route which has been redeployed.
		if(err.message.startsWith("Failed to fetch dynamically imported module")) {
			window.location.reload();
			return;
		}

		if(this.#errorReported) {
			return;
		}

		this.#errorReported = true;
		const time = new UTCDate().toISOString();

		this.hasError = true;
		this.error = err;

		const stack = await this.#getStack(err);
		const url = this.#getUrl();
		const user = this.#getUser();

		const details = serializeError(err) as Record<string, unknown>;
		this.errorDetails = {
			name: err.name,
			message: err.message,
			time,
			details,
			url: this.#getUrl(),
			user: this.#getUser(),
			gql: (err instanceof UnhandledGraphQLError) ? err.graphQLError : undefined,
			stack,
		};

		const errorReport: ErrorReport = {
			source,
			name: err.name,
			message: err.message,
			details,
			url,
			user,
			stack,
		};
		await this.#api.mutateOnce(reportErrorMutation, { errorReport });
	}

	async reportError(message: string): Promise<void> {
		await this.#api.mutation(reportErrorMutation)
			.submit({
				errorReport: {
					source: "REPORT",
					message,
					url: this.#getUrl(),
					user: this.#getUser(),
					stack: (await this.#getStack(new Error())).slice(1),
				},
			});
	}

	static isUpstream503Error(error: unknown): boolean {
		return isGraphqlError(error)
			&& error.extensions.code === "UPSTREAM"
			&& error.extensions["statusCode"] === 503;
	}
}
